import React, { useEffect, useRef, useState } from "react";
import {
  ChevronDownIcon,
  FolderUpIcon,
  MoreVerticalIcon,
  PlusIcon,
  SearchIcon,
} from "lucide-react";
import { StatusBadge } from "../tuition/StatusBadge";
import { AddEmployeeForm, type NewEmployeeData } from "./AddEmployeeForm";
import { ImportCSVModal } from "./ImportCSVModal";
import { PositionForm } from "./PositionForm";
import { StipendForm } from "./StipendForm";
import { SuspendEmployeeModal } from "./SuspendEmployeeModal";
import type {
  PayrollPositionDraft,
  PayrollStipendDraft,
  PayrollUiRosterEmployee,
} from "../../types";

type EmployeeRosterTableProps = {
  employees: PayrollUiRosterEmployee[];
  onOpenEmployee: (employeeId: string) => void;
  onAddEmployee?: (employee: NewEmployeeData) => void | Promise<void>;
  onImportCsv?: (file: File) => void | Promise<void>;
  onAddPosition?: (
    employeeId: string,
    position: PayrollPositionDraft,
  ) => void | Promise<void>;
  onAddStipend?: (
    employeeId: string,
    stipend: PayrollStipendDraft,
  ) => void | Promise<void>;
  onSuspendEmployee?: (
    employeeId: string,
    reason: string,
  ) => void | Promise<void>;
};

type SelectedEmployee = PayrollUiRosterEmployee;

const INITIAL_COL_WIDTHS = {
  checkbox: 48,
  name: 180,
  employeeId: 100,
  employmentType: 120,
  track: 120,
  status: 100,
  positionCount: 100,
  annualComp: 120,
  actions: 48,
};

const BulkActionToolbar = ({
  selectedCount,
  onClear,
}: {
  selectedCount: number;
  onClear: () => void;
}) => {
  if (selectedCount === 0) {
    return null;
  }

  return (
    <div className="mb-3 flex items-center justify-between rounded-[10px] border border-border bg-muted/40 px-4 py-3">
      <p className="font-body text-sm font-medium text-iron-grey">
        {selectedCount} employee{selectedCount === 1 ? "" : "s"} selected
      </p>
      <button
        type="button"
        onClick={onClear}
        className="font-body text-[13px] font-medium text-primary transition-colors hover:text-primary/80"
      >
        Clear selection
      </button>
    </div>
  );
};

export function EmployeeRosterTable({
  employees,
  onOpenEmployee,
  onAddEmployee,
  onImportCsv,
  onAddPosition,
  onAddStipend,
  onSuspendEmployee,
}: EmployeeRosterTableProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<Set<string>>(new Set());
  const [trackFilter, setTrackFilter] = useState<Set<string>>(new Set());
  const [statusFilter, setStatusFilter] = useState<Set<string>>(
    new Set(["Active", "On Leave"]),
  );
  const [activeFilterMenu, setActiveFilterMenu] = useState<
    "type" | "track" | "status" | null
  >(null);
  const [colWidths, setColWidths] = useState(INITIAL_COL_WIDTHS);
  const [isAddEmployeeOpen, setIsAddEmployeeOpen] = useState(false);
  const [isImportCsvOpen, setIsImportCsvOpen] = useState(false);
  const [isPositionFormOpen, setIsPositionFormOpen] = useState(false);
  const [isStipendFormOpen, setIsStipendFormOpen] = useState(false);
  const [isSuspendModalOpen, setIsSuspendModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<SelectedEmployee | null>(
    null,
  );

  const resizingCol = useRef<keyof typeof INITIAL_COL_WIDTHS | null>(null);
  const startX = useRef(0);
  const startWidth = useRef(0);
  const menuRef = useRef<HTMLDivElement>(null);
  const filterContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setActiveMenuId(null);
      }
      if (
        filterContainerRef.current &&
        !filterContainerRef.current.contains(event.target as Node)
      ) {
        setActiveFilterMenu(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (!resizingCol.current) {
        return;
      }
      const diff = event.clientX - startX.current;
      const newWidth = Math.max(40, startWidth.current + diff);
      setColWidths((previous) => ({
        ...previous,
        [resizingCol.current as string]: newWidth,
      }));
    };

    const handleMouseUp = () => {
      if (!resizingCol.current) {
        return;
      }
      resizingCol.current = null;
      document.body.style.cursor = "default";
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const handleResizeStart = (
    event: React.MouseEvent,
    colId: keyof typeof INITIAL_COL_WIDTHS,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    resizingCol.current = colId;
    startX.current = event.clientX;
    startWidth.current = colWidths[colId];
    document.body.style.cursor = "col-resize";
  };

  const filteredEmployees = employees
    .filter((employee) => {
      const query = searchQuery.toLowerCase();
      return (
        `${employee.firstName} ${employee.lastName}`.toLowerCase().includes(query) ||
        employee.employeeId.toLowerCase().includes(query)
      );
    })
    .filter(
      (employee) =>
        typeFilter.size === 0 || typeFilter.has(employee.employmentType),
    )
    .filter(
      (employee) => trackFilter.size === 0 || trackFilter.has(employee.track),
    )
    .filter(
      (employee) => statusFilter.size === 0 || statusFilter.has(employee.status),
    );

  const toggleAll = () => {
    if (selectedRows.size === filteredEmployees.length) {
      setSelectedRows(new Set());
      return;
    }
    setSelectedRows(new Set(filteredEmployees.map((employee) => employee.id)));
  };

  const toggleRow = (employeeId: string) => {
    setSelectedRows((previous) => {
      const next = new Set(previous);
      if (next.has(employeeId)) {
        next.delete(employeeId);
      } else {
        next.add(employeeId);
      }
      return next;
    });
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(value);

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "Active":
        return "Current";
      case "On Leave":
        return "Pending";
      case "Terminated":
        return "Archived";
      default:
        return "Archived";
    }
  };

  const resetSelectedEmployee = () => {
    setSelectedEmployee(null);
    setIsPositionFormOpen(false);
    setIsStipendFormOpen(false);
    setIsSuspendModalOpen(false);
  };

  const handleRowClick = (
    employeeId: string,
    event: React.MouseEvent<HTMLTableRowElement>,
  ) => {
    const target = event.target as HTMLElement;
    if (
      target.closest('input[type="checkbox"]') ||
      target.closest("button") ||
      target.closest(".actions-menu")
    ) {
      return;
    }
    onOpenEmployee(employeeId);
  };

  const ResizableHeader = ({
    id,
    label,
    align = "left",
  }: {
    id: keyof typeof INITIAL_COL_WIDTHS;
    label: React.ReactNode;
    align?: "left" | "center" | "right";
  }) => (
    <th
      className={`relative select-none px-4 py-3 font-body text-xs font-medium uppercase tracking-wider text-text-secondary text-${align}`}
      style={{
        width: colWidths[id],
        minWidth: colWidths[id],
        maxWidth: colWidths[id],
      }}
    >
      <div className="truncate">{label}</div>
      <div
        className="absolute right-0 top-0 bottom-0 z-10 w-1 cursor-col-resize hover:bg-primary/30"
        onMouseDown={(event) => handleResizeStart(event, id)}
      />
    </th>
  );

  return (
    <div className="w-full">
      <BulkActionToolbar
        selectedCount={selectedRows.size}
        onClear={() => setSelectedRows(new Set())}
      />

      <div className="mb-[24px] flex flex-col overflow-hidden rounded-[12px] border border-border bg-surface-light shadow-sm">
        <div className="flex flex-col justify-between gap-4 border-b border-border p-4 sm:flex-row sm:items-center">
          <div className="relative w-full flex-shrink-0 sm:w-64">
            <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary" />
            <input
              type="text"
              placeholder="Search name or ID..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className="w-full rounded-[6px] border border-border bg-background-light py-2 pl-9 pr-4 font-body text-sm text-iron-grey transition-all focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          <div
            className="flex flex-wrap items-center gap-2"
            ref={filterContainerRef}
          >
            {(
              [
                {
                  id: "type",
                  label: "Type",
                  options: ["Full-Time", "Part-Time", "Hourly", "Contractor"],
                  values: typeFilter,
                  setValues: setTypeFilter,
                },
                {
                  id: "track",
                  label: "Track",
                  options: ["Judaic", "General", "Both", "N/A"],
                  values: trackFilter,
                  setValues: setTrackFilter,
                },
                {
                  id: "status",
                  label: "Status",
                  options: ["Active", "On Leave", "Terminated"],
                  values: statusFilter,
                  setValues: setStatusFilter,
                },
              ] as const
            ).map((filter) => (
              <div className="relative" key={filter.id}>
                <button
                  type="button"
                  onClick={() =>
                    setActiveFilterMenu((current) =>
                      current === filter.id ? null : filter.id,
                    )
                  }
                  className={`flex items-center gap-2 rounded-[6px] border px-3 py-2 font-body text-[13px] font-medium transition-colors ${
                    filter.values.size > 0
                      ? "border-primary text-primary"
                      : "border-border text-iron-grey hover:bg-muted"
                  }`}
                >
                  {filter.label}
                  {filter.values.size > 0 ? ` (${filter.values.size})` : ""}
                  <ChevronDownIcon className="h-4 w-4 text-text-secondary" />
                </button>
                {activeFilterMenu === filter.id ? (
                  <div className="absolute left-0 top-full z-[100] mt-1 w-48 rounded-[8px] border border-border bg-surface-light py-1 shadow-lg animate-in fade-in zoom-in-95 duration-100">
                    {filter.options.map((option) => (
                      <label
                        key={option}
                        className="flex cursor-pointer items-center gap-2 px-4 py-2 font-body text-[13px] text-iron-grey hover:bg-muted"
                      >
                        <input
                          type="checkbox"
                          checked={filter.values.has(option)}
                          onChange={() => {
                            filter.setValues((previous) => {
                              const next = new Set(previous);
                              if (next.has(option)) {
                                next.delete(option);
                              } else {
                                next.add(option);
                              }
                              return next;
                            });
                          }}
                          className="h-4 w-4 cursor-pointer rounded border-border text-primary focus:ring-primary"
                        />
                        {option}
                      </label>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}

            <div className="mx-1 hidden h-6 w-px bg-border sm:block" />

            <button
              type="button"
              onClick={() => setIsImportCsvOpen(true)}
              className="flex items-center gap-2 rounded-[6px] border border-border bg-surface-light px-3 py-2 font-body text-[13px] font-medium text-iron-grey transition-colors hover:bg-muted"
            >
              <FolderUpIcon className="h-4 w-4 text-text-secondary" />
              Import CSV
            </button>
            <button
              type="button"
              onClick={() => setIsAddEmployeeOpen(true)}
              className="flex items-center gap-2 rounded-[6px] bg-primary px-3 py-2 font-body text-[13px] font-medium text-white shadow-sm transition-colors hover:bg-primary-hover"
            >
              <PlusIcon className="h-4 w-4" />
              Add Employee
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full table-fixed border-collapse text-left">
            <thead>
              <tr className="border-b border-border bg-muted">
                <th
                  className="relative w-12 px-4 py-3"
                  style={{
                    width: colWidths.checkbox,
                    minWidth: colWidths.checkbox,
                    maxWidth: colWidths.checkbox,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={
                      selectedRows.size === filteredEmployees.length &&
                      filteredEmployees.length > 0
                    }
                    onChange={toggleAll}
                    className="h-4 w-4 cursor-pointer rounded border-border text-primary focus:ring-primary"
                  />
                  <div
                    className="absolute right-0 top-0 bottom-0 z-10 w-1 cursor-col-resize hover:bg-primary/30"
                    onMouseDown={(event) => handleResizeStart(event, "checkbox")}
                  />
                </th>
                <ResizableHeader id="name" label="Employee Name" />
                <ResizableHeader id="employeeId" label="ID" />
                <ResizableHeader id="employmentType" label="Type" />
                <ResizableHeader id="track" label="Track" />
                <ResizableHeader id="status" label="Status" />
                <ResizableHeader
                  id="positionCount"
                  label="Positions"
                  align="center"
                />
                <ResizableHeader
                  id="annualComp"
                  label="Annual Comp"
                  align="right"
                />
                <th
                  className="px-4 py-3"
                  style={{
                    width: colWidths.actions,
                    minWidth: colWidths.actions,
                    maxWidth: colWidths.actions,
                  }}
                />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredEmployees.map((employee) => (
                <tr
                  key={employee.id}
                  onClick={(event) => handleRowClick(employee.id, event)}
                  data-testid={`payroll-employee-row-${employee.id}`}
                  className="group cursor-pointer transition-colors hover:bg-[#fbf9fa]"
                >
                  <td className="px-4 py-3" style={{ width: colWidths.checkbox }}>
                    <input
                      type="checkbox"
                      checked={selectedRows.has(employee.id)}
                      onChange={() => toggleRow(employee.id)}
                      className="h-4 w-4 cursor-pointer rounded border-border text-primary focus:ring-primary"
                    />
                  </td>
                  <td
                    className="truncate px-4 py-3"
                    style={{ width: colWidths.name }}
                  >
                    <span className="font-body text-sm font-semibold text-primary">
                      {employee.lastName}, {employee.firstName}
                    </span>
                  </td>
                  <td
                    className="truncate px-4 py-3 font-body text-sm text-text-secondary"
                    style={{ width: colWidths.employeeId }}
                  >
                    {employee.employeeId}
                  </td>
                  <td
                    className="truncate px-4 py-3 font-body text-sm text-iron-grey"
                    style={{ width: colWidths.employmentType }}
                  >
                    {employee.employmentType}
                  </td>
                  <td
                    className="truncate px-4 py-3 font-body text-sm text-iron-grey"
                    style={{ width: colWidths.track }}
                  >
                    {employee.track}
                  </td>
                  <td className="px-4 py-3" style={{ width: colWidths.status }}>
                    <StatusBadge
                      status={getStatusBadgeVariant(employee.status) as any}
                      size="sm"
                    />
                  </td>
                  <td
                    className="px-4 py-3 text-center font-body text-sm text-iron-grey"
                    style={{ width: colWidths.positionCount }}
                  >
                    {employee.positionCount}
                  </td>
                  <td
                    className="truncate px-4 py-3 text-right font-body text-sm font-medium text-iron-grey"
                    style={{ width: colWidths.annualComp }}
                  >
                    {employee.employmentType === "Hourly"
                      ? "Varies"
                      : formatCurrency(employee.annualCompTotal)}
                  </td>
                  <td
                    className="actions-menu relative px-4 py-3"
                    style={{ width: colWidths.actions }}
                  >
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        setActiveMenuId((current) =>
                          current === employee.id ? null : employee.id,
                        );
                      }}
                      data-testid={`payroll-employee-menu-${employee.id}`}
                      className="rounded-[6px] p-1 text-text-secondary transition-colors hover:bg-border/50 hover:text-iron-grey focus:outline-none"
                    >
                      <MoreVerticalIcon className="h-4 w-4" />
                    </button>
                    {activeMenuId === employee.id ? (
                      <div
                        ref={menuRef}
                        className="absolute right-8 top-10 z-[100] w-48 rounded-[8px] border border-border bg-surface-light py-1 shadow-lg animate-in fade-in zoom-in-95 duration-100"
                      >
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            setSelectedEmployee(employee);
                            setActiveMenuId(null);
                            setIsPositionFormOpen(true);
                          }}
                          className="block w-full px-4 py-2 text-left font-body text-[13px] text-iron-grey transition-colors hover:bg-muted"
                        >
                          Add position
                        </button>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            setSelectedEmployee(employee);
                            setActiveMenuId(null);
                            setIsStipendFormOpen(true);
                          }}
                          className="block w-full px-4 py-2 text-left font-body text-[13px] text-iron-grey transition-colors hover:bg-muted"
                        >
                          Add stipend
                        </button>
                        <div className="my-1 h-px bg-border" />
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            setSelectedEmployee(employee);
                            setActiveMenuId(null);
                            setIsSuspendModalOpen(true);
                          }}
                          className="block w-full px-4 py-2 text-left font-body text-[13px] text-strawberry transition-colors hover:bg-error-background"
                        >
                          Suspend
                        </button>
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
              {filteredEmployees.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center">
                    <p className="font-body text-sm text-text-secondary">
                      No employees found matching your filters.
                    </p>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between border-t border-border bg-background-light p-4">
          <p className="font-body text-[13px] text-text-secondary">
            Showing{" "}
            <span className="font-medium text-iron-grey">
              {filteredEmployees.length}
            </span>{" "}
            employees
          </p>
        </div>
      </div>

      <AddEmployeeForm
        isOpen={isAddEmployeeOpen}
        onClose={() => setIsAddEmployeeOpen(false)}
        onSave={(input) => void onAddEmployee?.(input)}
      />
      <ImportCSVModal
        isOpen={isImportCsvOpen}
        onClose={() => setIsImportCsvOpen(false)}
        onImport={(file) => void onImportCsv?.(file)}
      />

      {selectedEmployee ? (
        <>
          <PositionForm
            isOpen={isPositionFormOpen}
            onClose={resetSelectedEmployee}
            employeeName={`${selectedEmployee.firstName} ${selectedEmployee.lastName}`}
            onSave={(input) =>
              void onAddPosition?.(selectedEmployee.id, input)
            }
          />
          <StipendForm
            isOpen={isStipendFormOpen}
            onClose={resetSelectedEmployee}
            employeeName={`${selectedEmployee.firstName} ${selectedEmployee.lastName}`}
            onSave={(input) =>
              void onAddStipend?.(selectedEmployee.id, input)
            }
          />
          <SuspendEmployeeModal
            isOpen={isSuspendModalOpen}
            onClose={resetSelectedEmployee}
            employeeName={`${selectedEmployee.firstName} ${selectedEmployee.lastName}`}
            onConfirm={(reason) =>
              void onSuspendEmployee?.(selectedEmployee.id, reason)
            }
          />
        </>
      ) : null}
    </div>
  );
}
